/** Pin something at the top of the person's page: what, for how long, and how it will look there. */
import { useState } from "react";
import type { PinTarget } from "../../shared/types";
import { DaySheet } from "../components/DaySheet";
import { Button } from "../components/ui/Button";
import { Icon, type IconName } from "../components/ui/Icon";
import { Seg } from "../components/ui/Seg";
import { Sheet } from "../components/ui/Sheet";
import { dateIn, endOfDay, pinDay } from "./when";

/** One thing the host can pin for this person. */
export interface PinItem {
  key: string;
  kind: PinTarget;
  id: string;
  /** The registration and room the line belongs to: the pin is made for that one. */
  registrantId: string;
  webinarId: string;
  label: string;
  sub: string;
  icon: IconName;
}

export interface PinChoice {
  item: PinItem;
  /** Unix seconds, or null until it is unpinned. */
  until: number | null;
}

export function PinSheet({ open, first, hostFirst, items, start, current, onClose, onPin }: {
  open: boolean;
  first: string;
  hostFirst: string;
  items: PinItem[];
  /** The item the sheet opens on: the one whose pin was pressed. */
  start: string | null;
  /** What is pinned for them now, which the new pin takes the place of. */
  current: { key: string; until: number | null } | null;
  onClose: () => void;
  onPin: (choice: PinChoice) => Promise<void>;
}) {
  // Why no reset on open: the sheet is mounted when it opens, so each opening starts from what was pressed.
  const [key, setKey] = useState<string | null>(() => start ?? current?.key ?? items[0]?.key ?? null);
  const [dated, setDated] = useState(() => !!current?.until);
  const [day, setDay] = useState(() => dateIn(7));
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const sel = items.find((x) => x.key === key) ?? items[0] ?? null;
  const until = dated ? endOfDay(day) : null;

  const pin = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await onPin({ item: sel, until });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Pin for ${first}`}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="p" icon="pin" onClick={pin} disabled={!sel || busy || (dated && !until)}>
            Pin it
          </Button>
        </>
      }
    >
      <div className="sheet-b col" style={{ gap: 16 }}>
        <div className="field">
          <div className="lbl">What to pin</div>
          {items.length ? (
            <div className="cp-list" role="radiogroup" aria-label="What to pin">
              {items.map((x) => (
                <button
                  type="button"
                  role="radio"
                  aria-checked={sel?.key === x.key}
                  key={x.key}
                  className={`cp-row${sel?.key === x.key ? " on" : ""}`}
                  onClick={() => setKey(x.key)}
                >
                  <span className="rd" />
                  <Icon name={x.icon} />
                  <span className="sp">
                    <b>{x.label}</b>
                    <span>{x.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="small muted">Nothing to pin yet.</div>
          )}
        </div>
        <div className="field">
          <div className="lbl">For how long</div>
          <div className="cp-until">
            <Seg
              label="For how long"
              value={dated ? "date" : "open"}
              onChange={(v) => setDated(v === "date")}
              options={[
                { value: "open", label: "Until I unpin it" },
                { value: "date", label: "Until a date" },
              ]}
            />
            {dated ? (
              <button type="button" className="inp mk-day" aria-label={`Until, ${until ? pinDay(until) : "pick a day"}`} onClick={() => setPicking(true)}>
                <Icon name="cal" />
                <span>{until ? pinDay(until) : "Pick a day"}</span>
              </button>
            ) : null}
          </div>
        </div>
        {sel ? (
          <div className="field">
            <div className="lbl">At the top of <bdi>{first}</bdi>’s page</div>
            <div className="cp-pv">
              <Icon name="pin" />
              <div className="sp">
                <small>Pinned by <bdi>{hostFirst}</bdi></small>
                <b>{sel.label}</b>
              </div>
            </div>
          </div>
        ) : null}
        {current ? (
          <div className="note">
            <Icon name="info" />
            <div>It takes the place of what’s pinned now.</div>
          </div>
        ) : null}
      </div>
      {picking ? <DaySheet value={day} min={dateIn(1)} onPick={setDay} onClose={() => setPicking(false)} /> : null}
    </Sheet>
  );
}
